import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Route, RoutePayload } from '../../core/models/route.model';
import { RouteStop, RouteStopAddress, RouteStopPayload } from '../../core/models/route-stop.model';
import { Organisation } from '../../core/models/organisation.model';
import { StatusLookup } from '../../core/models/lookup.model';
import { User } from '../../core/models/user.model';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { StatusID } from '../../core/enums/status-id.enum';
import { AuthService } from '../../core/services/auth.service';
import { LookupService } from '../../core/services/lookup.service';
import { OrganisationService } from '../../core/services/organisation.service';
import { RouteService } from '../../core/services/route.service';
import { SavedStop, SavedStopPayload } from '../../core/models/saved-stop.model';
import { SavedStopService } from '../../core/services/saved-stop.service';
import { ConfirmationService } from '../../core/services/confirmation.service';

@Component({
  selector: 'app-route-management',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './route-management.component.html',
  styleUrls: ['./management-page.component.css']
})
export class RouteManagementComponent implements OnInit {
  routes: Route[] = [];
  organisations: Organisation[] = [];
  statuses: StatusLookup[] = [
    { statusID: StatusID.ACTIVE, statusName: 'Active' },
    { statusID: StatusID.INACTIVE, statusName: 'Inactive' }
  ];
  selectedRoute: Route | null = null;
  selectedRouteStops: RouteStop[] = [];
  savedStops: SavedStop[] = [];
  selectedOrganisationId: number | null = null;
  includeInactive = false;
  showRouteForm = false;
  showStopForm = false;
  showSavedStopPicker = false;
  showSavedStopForm = false;
  isEditingRoute = false;
  isEditingStop = false;
  isLoading = false;
  isSavingRoute = false;
  isSavingStop = false;
  isSavingSavedStop = false;
  selectedSavedStopId: number | null = null;
  savedStopOrder: number | null = null;
  errorMessage = '';
  successMessage = '';
  routeNameError = '';
  stopOrderError = '';

  routeForm: FormGroup;
  stopForm: FormGroup;
  savedStopForm: FormGroup;

  private readonly currentUser: User | null;
  readonly currentRole: UserRole;
  readonly isAdminOrPlatform: boolean;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private lookupService: LookupService,
    private organisationService: OrganisationService,
    private routeService: RouteService,
    private savedStopService: SavedStopService,
    private confirmations: ConfirmationService
  ) {
    this.currentUser = this.authService.getCurrentUser();
    this.currentRole = normalizeUserRole(
      this.currentUser?.userRoleName || this.currentUser?.userRole || this.currentUser?.userRoleID
    );
    this.isAdminOrPlatform = this.currentRole === UserRole.ADMIN || this.currentRole === UserRole.PLATFORM_EMPLOYEE;

    this.routeForm = this.fb.group({
      routeID: [0],
      routeName: ['', [Validators.required, Validators.maxLength(120)]],
      description: [''],
      organisationID: [null, Validators.required],
      statusID: [StatusID.ACTIVE, Validators.required]
    });

    this.stopForm = this.fb.group({
      routeStopID: [0],
      savedStopID: [null],
      saveForReuse: [true],
      stopName: ['', [Validators.required, Validators.maxLength(140)]],
      stopLatitude: [null, [Validators.required, Validators.min(-90), Validators.max(90)]],
      stopLongitude: [null, [Validators.required, Validators.min(-180), Validators.max(180)]],
      stopOrder: [null, [Validators.required, Validators.min(1)]],
      hasAddress: [false],
      address: this.fb.group({
        streetAddress: [''],
        suburb: [''],
        city: [''],
        province: [''],
        postalCode: [''],
        country: ['']
      })
    });

    this.savedStopForm = this.fb.group({
      stopName: ['', [Validators.required, Validators.maxLength(140)]],
      stopLatitude: [null, [Validators.required, Validators.min(-90), Validators.max(90)]],
      stopLongitude: [null, [Validators.required, Validators.min(-180), Validators.max(180)]],
      streetAddress: [''],
      suburb: [''],
      city: [''],
      province: [''],
      postalCode: [''],
      country: ['South Africa']
    });
  }

  ngOnInit(): void {
    this.selectedOrganisationId = this.isAdminOrPlatform ? null : this.currentUser?.organisationID || null;
    this.routeForm.patchValue({ organisationID: this.selectedOrganisationId });
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      statuses: this.lookupService.getStatuses().pipe(catchError(() => of(this.statuses))),
      organisations: this.isAdminOrPlatform
        ? this.organisationService.getOrganisations().pipe(catchError(() => of([])))
        : of([]),
      routes: this.routeService.getRoutes({
        organisationId: this.selectedOrganisationId,
        includeInactive: this.includeInactive
      }).pipe(catchError(() => {
        this.errorMessage = 'Unable to load routes right now.';
        return of([]);
      }))
    }).pipe(finalize(() => this.isLoading = false)).subscribe(({ statuses, organisations, routes }) => {
      const routeStatuses = statuses.filter(status => [StatusID.ACTIVE, StatusID.INACTIVE].includes(status.statusID));
      this.statuses = routeStatuses.length ? routeStatuses : this.statuses;
      this.organisations = organisations;
      this.routes = this.orderRoutes(routes);
      this.syncSelectedRoute();
    });
  }

  loadRoutes(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.routeService.getRoutes({
      organisationId: this.selectedOrganisationId,
      includeInactive: this.includeInactive
    }).pipe(finalize(() => this.isLoading = false)).subscribe({
      next: routes => {
        this.routes = this.orderRoutes(routes);
        this.syncSelectedRoute();
      },
      error: () => this.errorMessage = 'Unable to load routes right now.'
    });
  }

  selectRoute(route: Route): void {
    this.routeService.getRouteById(route.routeID).subscribe({
      next: detail => {
        this.selectedRoute = detail;
        this.selectedRouteStops = this.getOrderedStops(detail);
        this.closeEditors();
        this.loadSavedStops();
      },
      error: () => this.errorMessage = 'Unable to load route detail.'
    });
  }

  startCreateRoute(): void {
    this.isEditingRoute = false;
    this.showRouteForm = true;
    this.showStopForm = false;
    this.routeNameError = '';
    this.routeForm.reset({
      routeID: 0,
      routeName: '',
      description: '',
      organisationID: this.selectedOrganisationId,
      statusID: StatusID.ACTIVE
    });
    if (!this.isAdminOrPlatform) {
      this.routeForm.get('organisationID')?.disable();
    } else {
      this.routeForm.get('organisationID')?.enable();
    }
  }

  startEditRoute(route: Route): void {
    this.selectedRoute = route;
    this.isEditingRoute = true;
    this.showRouteForm = true;
    this.showStopForm = false;
    this.routeNameError = '';
    this.routeForm.reset({
      routeID: route.routeID,
      routeName: route.routeName,
      description: route.description || '',
      organisationID: route.organisationID,
      statusID: route.statusID || StatusID.ACTIVE
    });
    this.routeForm.get('organisationID')?.disable();
  }

  saveRoute(): void {
    this.routeNameError = '';
    this.errorMessage = '';
    this.successMessage = '';
    this.routeForm.markAllAsTouched();

    if (this.routeForm.invalid) {
      return;
    }

    const raw = this.routeForm.getRawValue();
    const payload: RoutePayload = {
      routeID: Number(raw.routeID || 0),
      routeName: String(raw.routeName || '').trim(),
      description: String(raw.description || '').trim(),
      organisationID: Number(raw.organisationID),
      statusID: Number(raw.statusID)
    };

    this.isSavingRoute = true;
    const request = this.isEditingRoute && payload.routeID
      ? this.routeService.updateRoute(payload.routeID, payload)
      : this.routeService.createRoute(payload);

    request.pipe(finalize(() => this.isSavingRoute = false)).subscribe({
      next: route => {
        this.successMessage = this.isEditingRoute ? 'Route updated.' : 'Route created.';
        this.showRouteForm = false;
        this.loadRoutes();
        this.selectRoute(route);
      },
      error: error => {
        if (this.isDuplicateError(error)) {
          this.routeNameError = 'A route with this name already exists for this organisation.';
          this.routeForm.get('routeName')?.setErrors({ duplicate: true });
          return;
        }
        this.errorMessage = 'Unable to save the route.';
      }
    });
  }

  async archiveRoute(route: Route): Promise<void> {
    if (!await this.confirmations.confirm({
      title: 'Archive route',
      message: `Archive ${route.routeName}? It will become inactive and cannot be assigned to new trips.`,
      confirmLabel: 'Archive',
      tone: 'danger'
    })) return;

    this.routeService.deleteRoute(route.routeID).subscribe({
      next: () => {
        this.successMessage = 'Route archived.';
        this.loadRoutes();
      },
      error: () => this.errorMessage = 'Unable to archive the route.'
    });
  }

  async duplicateRoute(route: Route): Promise<void> {
    if (!await this.confirmations.confirm({
      title: 'Duplicate route',
      message: `Duplicate ${route.routeName} and all of its stops?`,
      confirmLabel: 'Duplicate'
    })) return;
    this.routeService.duplicateRoute(route.routeID).subscribe({
      next: duplicate => {
        this.successMessage = `Route duplicated as ${duplicate.routeName}.`;
        this.loadRoutes();
        this.selectRoute(duplicate);
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to duplicate the route.')
    });
  }

  startCreateStop(): void {
    if (!this.selectedRoute) {
      return;
    }

    this.isEditingStop = false;
    this.showStopForm = true;
    this.showSavedStopPicker = false;
    this.showSavedStopForm = false;
    this.showRouteForm = false;
    this.stopOrderError = '';
    this.stopForm.reset({
      routeStopID: 0,
      savedStopID: null,
      saveForReuse: true,
      stopName: '',
      stopLatitude: null,
      stopLongitude: null,
      stopOrder: this.nextStopOrder(),
      hasAddress: false,
      address: {
        streetAddress: '',
        suburb: '',
        city: '',
        province: '',
        postalCode: '',
        country: ''
      }
    });
    this.setAddressValidators(false);
  }

  startEditStop(stop: RouteStop): void {
    this.isEditingStop = true;
    this.showStopForm = true;
    this.showSavedStopPicker = false;
    this.showSavedStopForm = false;
    this.showRouteForm = false;
    this.stopOrderError = '';
    const address = stop.address || {};
    const hasAddress = this.hasAddress(address);
    this.stopForm.reset({
      routeStopID: stop.routeStopID,
      savedStopID: stop.savedStopID || null,
      saveForReuse: Boolean(stop.savedStopID),
      stopName: stop.stopName,
      stopLatitude: stop.stopLatitude,
      stopLongitude: stop.stopLongitude,
      stopOrder: stop.stopOrder,
      hasAddress,
      address: {
        streetAddress: address.streetAddress || '',
        suburb: address.suburb || '',
        city: address.city || '',
        province: address.province || '',
        postalCode: address.postalCode || '',
        country: address.country || ''
      }
    });
    this.setAddressValidators(hasAddress);
  }

  onAddressToggle(): void {
    const hasAddress = Boolean(this.stopForm.get('hasAddress')?.value);
    this.setAddressValidators(hasAddress);
    if (!hasAddress) {
      this.stopForm.get('address')?.reset({
        streetAddress: '',
        suburb: '',
        city: '',
        province: '',
        postalCode: '',
        country: ''
      });
    }
  }

  saveStop(): void {
    if (!this.selectedRoute) {
      return;
    }

    this.stopOrderError = '';
    this.errorMessage = '';
    this.successMessage = '';
    this.stopForm.markAllAsTouched();

    if (this.stopForm.invalid) {
      return;
    }

    const raw = this.stopForm.value;
    const normalizedStopName = String(raw.stopName || '').trim().toUpperCase();
    const existingSavedStop = !this.isEditingStop && raw.saveForReuse
      ? this.savedStops.find(stop => stop.stopName.trim().toUpperCase() === normalizedStopName)
      : undefined;
    const payload: RouteStopPayload = {
      routeStopID: Number(raw.routeStopID || 0),
      savedStopID: existingSavedStop?.savedStopID || (raw.savedStopID ? Number(raw.savedStopID) : null),
      saveForReuse: !existingSavedStop && !this.isEditingStop && Boolean(raw.saveForReuse),
      stopName: normalizedStopName,
      stopLatitude: Number(raw.stopLatitude),
      stopLongitude: Number(raw.stopLongitude),
      stopOrder: Number(raw.stopOrder),
      routeID: this.selectedRoute.routeID,
      address: raw.hasAddress ? this.cleanAddress(raw.address) : null
    };

    this.isSavingStop = true;
    const request = this.isEditingStop && payload.routeStopID
      ? this.routeService.updateRouteStop(payload.routeStopID, payload)
      : this.routeService.addRouteStop(payload);

    request.pipe(finalize(() => this.isSavingStop = false)).subscribe({
      next: () => {
        this.successMessage = this.isEditingStop ? 'Stop updated.' : 'Stop added.';
        this.showStopForm = false;
        this.refreshSelectedRoute();
      },
      error: error => {
        if (this.isDuplicateError(error)) {
          const message = this.extractError(error, '');
          if (message.toLowerCase().includes('saved stop')) {
            this.errorMessage = message;
            return;
          }
          this.stopOrderError = 'This stop order is already used on the route.';
          this.stopForm.get('stopOrder')?.setErrors({ duplicate: true });
          return;
        }
        this.errorMessage = 'Unable to save the stop.';
      }
    });
  }

  async deleteStop(stop: RouteStop): Promise<void> {
    if (!await this.confirmations.confirm({
      title: 'Delete route stop',
      message: `Delete stop ${stop.stopOrder}: ${stop.stopName}?`,
      confirmLabel: 'Delete',
      tone: 'danger'
    })) return;

    this.routeService.deleteRouteStop(stop.routeStopID).subscribe({
      next: () => {
        this.successMessage = 'Stop deleted.';
        this.refreshSelectedRoute();
      },
      error: () => this.errorMessage = 'Unable to delete the stop.'
    });
  }

  saveRouteStopForReuse(stop: RouteStop): void {
    if (!this.selectedRoute || stop.savedStopID) return;
    const existingSavedStop = this.savedStops.find(item =>
      item.stopName.trim().toUpperCase() === stop.stopName.trim().toUpperCase()
    );
    const payload: RouteStopPayload = {
      routeStopID: stop.routeStopID,
      stopName: String(stop.stopName || '').trim().toUpperCase(),
      stopLatitude: Number(stop.stopLatitude),
      stopLongitude: Number(stop.stopLongitude),
      stopOrder: Number(stop.stopOrder),
      routeID: this.selectedRoute.routeID,
      savedStopID: existingSavedStop?.savedStopID || null,
      saveForReuse: !existingSavedStop,
      address: stop.address ? this.cleanAddress(stop.address) : null
    };

    this.isSavingSavedStop = true;
    this.routeService.updateRouteStop(stop.routeStopID, payload).pipe(
      finalize(() => this.isSavingSavedStop = false)
    ).subscribe({
      next: () => {
        this.successMessage = `${stop.stopName} is now available for reuse.`;
        this.refreshSelectedRoute();
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to save this stop for reuse.')
    });
  }

  startUseSavedStop(): void {
    if (!this.selectedRoute) return;
    this.showSavedStopPicker = true;
    this.showStopForm = false;
    this.showRouteForm = false;
    this.showSavedStopForm = false;
    this.selectedSavedStopId = null;
    this.savedStopOrder = this.nextStopOrder();
    this.stopOrderError = '';
    this.loadSavedStops();
  }

  addSavedStopToRoute(): void {
    if (!this.selectedRoute || !this.selectedSavedStopId || !this.savedStopOrder || this.savedStopOrder < 1) {
      this.stopOrderError = 'Select a saved stop and enter a valid stop order.';
      return;
    }
    this.isSavingSavedStop = true;
    this.savedStopService.addToRoute(this.selectedSavedStopId, {
      routeID: this.selectedRoute.routeID,
      savedStopID: this.selectedSavedStopId,
      stopOrder: this.savedStopOrder
    }).pipe(finalize(() => this.isSavingSavedStop = false)).subscribe({
      next: () => {
        this.successMessage = 'Saved stop added to the route.';
        this.showSavedStopPicker = false;
        this.refreshSelectedRoute();
      },
      error: error => {
        if (this.isDuplicateError(error)) this.stopOrderError = 'This stop order is already used on the route.';
        else this.errorMessage = this.extractError(error, 'Unable to add the saved stop to this route.');
      }
    });
  }

  startCreateSavedStop(): void {
    if (!this.selectedRoute) return;
    this.showSavedStopForm = true;
    this.showSavedStopPicker = false;
    this.showStopForm = false;
    this.showRouteForm = false;
    this.savedStopForm.reset({
      stopName: '', stopLatitude: null, stopLongitude: null, streetAddress: '', suburb: '',
      city: '', province: '', postalCode: '', country: 'South Africa'
    });
  }

  saveSavedStop(): void {
    this.savedStopForm.markAllAsTouched();
    if (!this.selectedRoute || this.savedStopForm.invalid) return;
    const raw = this.savedStopForm.getRawValue();
    const payload: SavedStopPayload = {
      organisationID: this.selectedRoute.organisationID,
      stopName: String(raw.stopName || '').trim().toUpperCase(),
      stopLatitude: Number(raw.stopLatitude),
      stopLongitude: Number(raw.stopLongitude),
      streetAddress: String(raw.streetAddress || '').trim(),
      suburb: String(raw.suburb || '').trim(),
      city: String(raw.city || '').trim(),
      province: String(raw.province || '').trim(),
      postalCode: String(raw.postalCode || '').trim(),
      country: String(raw.country || '').trim(),
      statusID: StatusID.ACTIVE
    };
    this.isSavingSavedStop = true;
    this.savedStopService.createSavedStop(payload).pipe(finalize(() => this.isSavingSavedStop = false)).subscribe({
      next: stop => {
        this.successMessage = `${stop.stopName} saved for reuse.`;
        this.showSavedStopForm = false;
        this.startUseSavedStop();
        this.selectedSavedStopId = stop.savedStopID;
      },
      error: error => this.errorMessage = this.extractError(error, 'Unable to save this reusable stop.')
    });
  }

  loadSavedStops(): void {
    if (!this.selectedRoute?.organisationID) {
      this.savedStops = [];
      return;
    }
    this.savedStopService.getSavedStops({ organisationId: this.selectedRoute.organisationID }).subscribe({
      next: stops => this.savedStops = stops,
      error: () => this.errorMessage = 'Unable to load saved stops.'
    });
  }

  getSavedStopLabel(stop: SavedStop): string {
    return `${stop.stopName} — ${stop.city || stop.streetAddress}`;
  }

  isSavedStopInvalid(controlName: string): boolean {
    const control = this.savedStopForm.get(controlName);
    return Boolean(control?.invalid && (control.touched || control.dirty));
  }

  refreshSelectedRoute(): void {
    if (this.selectedRoute) {
      this.selectRoute(this.selectedRoute);
    }
  }

  onOrganisationFilterChange(): void {
    this.loadRoutes();
  }

  getOrganisationOptionLabel(organisation: Organisation): string {
    return organisation.organisationName || organisation.orgName || `Organisation ${organisation.organisationID}`;
  }

  getRouteDescription(route: Route): string {
    return route.description || route.routeDescription || 'No description';
  }

  getStopAddress(stop: RouteStop): string {
    const address = stop.address;
    if (!address) {
      return 'No address captured';
    }

    return [
      address.streetAddress,
      address.suburb,
      address.city,
      address.province,
      address.postalCode,
      address.country
    ].filter(Boolean).join(', ') || 'No address captured';
  }

  isInactive(route: Route | null): boolean {
    return Number(route?.statusID) === StatusID.INACTIVE
      || String(route?.statusName || '').toLowerCase() === 'inactive';
  }

  isInvalid(controlName: string, form: FormGroup = this.routeForm): boolean {
    const control = form.get(controlName);
    return Boolean(control?.invalid && (control.touched || control.dirty));
  }

  isAddressInvalid(controlName: string): boolean {
    const control = this.stopForm.get(`address.${controlName}`);
    return Boolean(control?.invalid && (control.touched || control.dirty));
  }

  private setAddressValidators(hasAddress: boolean): void {
    ['streetAddress', 'city', 'country'].forEach(controlName => {
      const control = this.stopForm.get(`address.${controlName}`);
      control?.setValidators(hasAddress ? [Validators.required] : []);
      control?.updateValueAndValidity();
    });
  }

  private cleanAddress(address: RouteStopAddress): RouteStopAddress {
    return {
      streetAddress: String(address?.streetAddress || '').trim(),
      suburb: String(address?.suburb || '').trim(),
      city: String(address?.city || '').trim(),
      province: String(address?.province || '').trim(),
      postalCode: String(address?.postalCode || '').trim(),
      country: String(address?.country || '').trim()
    };
  }

  private hasAddress(address: RouteStopAddress): boolean {
    return Boolean(Object.values(address || {}).some(value => String(value || '').trim()));
  }

  private nextStopOrder(): number {
    return this.selectedRouteStops.length
      ? Math.max(...this.selectedRouteStops.map(stop => Number(stop.stopOrder) || 0)) + 1
      : 1;
  }

  private getOrderedStops(route: Route): RouteStop[] {
    return [...(route.stops || route.routeStops || [])]
      .sort((first, second) => Number(first.stopOrder) - Number(second.stopOrder));
  }

  private orderRoutes(routes: Route[]): Route[] {
    return [...routes].sort((first, second) => first.routeName.localeCompare(second.routeName));
  }

  private syncSelectedRoute(): void {
    if (!this.selectedRoute) {
      this.selectedRouteStops = [];
      return;
    }

    const matchingRoute = this.routes.find(route => route.routeID === this.selectedRoute?.routeID);
    if (!matchingRoute) {
      this.selectedRoute = null;
      this.selectedRouteStops = [];
      return;
    }

    this.selectRoute(matchingRoute);
  }

  private closeEditors(): void {
    this.showRouteForm = false;
    this.showStopForm = false;
    this.showSavedStopPicker = false;
    this.showSavedStopForm = false;
    this.routeNameError = '';
    this.stopOrderError = '';
  }

  private isDuplicateError(error: any): boolean {
    const message = [
      error?.error?.message,
      error?.error?.title,
      error?.error,
      error?.message
    ].filter(Boolean).join(' ').toLowerCase();

    return error?.status === 409 || message.includes('duplicate') || message.includes('already exists');
  }

  private extractError(error: any, fallback: string): string {
    return error?.error?.message
      || error?.error?.title
      || (typeof error?.error === 'string' ? error.error : '')
      || error?.message
      || fallback;
  }
}
